/**
 * The New Site wizard's hand-off to Baxter (AB#642).
 *
 * The wizard creates the site, uploads the media, then drops the whole first
 * turn in localStorage and navigates away — the panel picks it up on mount.
 * localStorage (not router state) because the navigation crosses a route that
 * remounts, and a reload must NOT resend the turn: reading CONSUMES the key.
 */

import type { AiSelectionContext } from '@wordpuppi/shared';

export interface BaxterBriefMedia {
  filename: string;
  url: string;
}

/** AB#810: the page bytes to put back if the person reverts that turn. */
export interface BaxterRevertPage {
  page_id: string;
  content: string | null;
  custom_css: string | null;
  editor_mode: string | null;
}

export interface BaxterBrief {
  /** Skill id (e.g. `love`). */
  skill: string;
  /** `provider:model`, exactly as the panel's picker stores it. */
  model: string;
  /** The fully formed first user message. */
  message: string;
  media: BaxterBriefMedia[];
  /**
   * AB#810: the canvas selection this ask is about. A brief carrying one
   * continues the OPEN chat instead of starting the wizard's fresh one.
   */
  selection?: AiSelectionContext;
  /** AB#810: what Revert on that turn restores. */
  previous?: BaxterRevertPage;
}

const memoryBriefs = new Map<string, BaxterBrief>();

/**
 * Fired right after a brief is stored, so a panel that is ALREADY mounted picks
 * it up (the mount-time read only covers a panel that opens for it). The site
 * shell listens too: it opens the rail. AB#810.
 */
export const BAXTER_BRIEF_EVENT = 'wpp-baxter-brief';

/** In-memory handoff still works when browser storage is disabled. */
export function writeBaxterBrief(siteSlug: string, brief: BaxterBrief): boolean {
  memoryBriefs.set(siteSlug, brief);
  let stored = true;
  try { localStorage.setItem(baxterBriefKey(siteSlug), JSON.stringify(brief)); }
  catch { stored = false; }
  window.dispatchEvent(new CustomEvent(BAXTER_BRIEF_EVENT, { detail: siteSlug }));
  return stored;
}

export const baxterBriefKey = (siteSlug: string) => `wpp-baxter-brief:${siteSlug}`;

/** Is a brief waiting? Cheap peek — does NOT consume it (the layout uses this to open the rail). */
export function hasBaxterBrief(siteSlug: string): boolean {
  try {
    return memoryBriefs.has(siteSlug) || localStorage.getItem(baxterBriefKey(siteSlug)) != null;
  } catch {
    return memoryBriefs.has(siteSlug); // storage disabled
  }
}

/** Read and REMOVE the brief. Returns null for missing/garbage; the key is gone either way. */
export function readBaxterBrief(siteSlug: string): BaxterBrief | null {
  const memory = memoryBriefs.get(siteSlug);
  memoryBriefs.delete(siteSlug);
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(baxterBriefKey(siteSlug));
    localStorage.removeItem(baxterBriefKey(siteSlug));
  } catch {
    return memory ?? null; // storage disabled
  }
  if (!raw) return memory ?? null;
  try {
    const b = JSON.parse(raw) as Partial<BaxterBrief>;
    if (typeof b?.message !== 'string' || !b.message.trim()) return null;
    return {
      skill: typeof b.skill === 'string' ? b.skill : '',
      model: typeof b.model === 'string' ? b.model : '',
      message: b.message,
      media: Array.isArray(b.media)
        ? b.media.filter((m): m is BaxterBriefMedia => typeof m?.url === 'string')
        : [],
      ...(b.selection && Array.isArray(b.selection.elements) ? { selection: b.selection } : {}),
      ...(b.previous?.page_id ? { previous: b.previous } : {}),
    };
  } catch {
    return null; // half-written / hand-edited key
  }
}
